import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { queryClient } from "@/lib/queryClient";
import type { AppRole } from "@/types/roles";

/** A client-portal login's context, derived from its client_users row. */
export type ClientContext = {
  clientId: string;
  mustChangePassword: boolean;
};

type AuthState = {
  session: Session | null;
  user: User | null;
  roles: AppRole[];
  /** Non-null only for client-portal logins. */
  client: ClientContext | null;
  loading: boolean;
};

type AuthContextValue = AuthState & {
  signOut: () => Promise<void>;
  refreshRoles: () => Promise<void>;
  /**
   * "Switch to Manager view" — a super-admin-only preview mode. When on,
   * `effectiveRoles` reports the user as a plain `admin` (a Manager) so the
   * console hides + blocks the super-admin-only and org-admin-only pages. Purely a UI
   * preview: the real session/JWT is unchanged, so RLS still grants the
   * super-admin their normal data access.
   */
  managerPreview: boolean;
  setManagerPreview: (on: boolean) => void;
  /** Roles after applying manager-preview. Use this for route/nav gating. */
  effectiveRoles: AppRole[];
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const PREVIEW_KEY = "sp.managerPreview";

async function fetchRoles(userId: string): Promise<AppRole[]> {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);

  if (error) {
    console.error("Failed to load user roles", error);
    return [];
  }
  return (data ?? []).map((row) => row.role as AppRole);
}

/** A client-portal login is identified by a client_users row (self-readable). */
async function fetchClientContext(userId: string): Promise<ClientContext | null> {
  const { data, error } = await supabase
    .from("client_users")
    .select("client_id, must_change_password")
    .eq("user_id", userId)
    .maybeSingle();
  if (error || !data) return null;
  return { clientId: data.client_id, mustChangePassword: data.must_change_password };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    session: null,
    user: null,
    roles: [],
    client: null,
    loading: true,
  });

  const applySession = useCallback(async (session: Session | null) => {
    if (!session?.user) {
      setState({ session: null, user: null, roles: [], client: null, loading: false });
      return;
    }
    // A client-portal login carries ONLY the "client" role (never a user_roles
    // row) and reaches only /client — so client detection wins and we don't
    // also grant staff/admin roles.
    const client = await fetchClientContext(session.user.id);
    const roles: AppRole[] = client ? ["client"] : await fetchRoles(session.user.id);
    setState({ session, user: session.user, roles, client, loading: false });
  }, []);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      void applySession(data.session);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      // Shared venue phones: wipe the in-memory query cache on any sign-out
      // (button, expired token, other-tab logout) so the next account never
      // sees the previous user's cached data. The durable offline write-queue
      // in IndexedDB is intentionally left intact.
      if (event === "SIGNED_OUT") {
        queryClient.clear();
        // Cache Storage outlives the tab — also drop the service worker's
        // Supabase caches so the next account can't replay this user's reads
        // or images. (Pages can delete SW caches directly; the per-user
        // cache-key partition in sw.ts is the primary guard, this is
        // defense-in-depth.)
        if ("caches" in window) {
          void window.caches.delete("supabase-api-cache").catch(() => {});
          void window.caches.delete("supabase-storage-cache").catch(() => {});
        }
      }
      void applySession(session);
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [applySession]);

  // Manager-preview flag (persisted). Only meaningful for super admins.
  const [managerPreview, setManagerPreviewState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(PREVIEW_KEY) === "1";
  });

  const setManagerPreview = useCallback((on: boolean) => {
    setManagerPreviewState(on);
    try {
      if (on) window.localStorage.setItem(PREVIEW_KEY, "1");
      else window.localStorage.removeItem(PREVIEW_KEY);
    } catch {
      // storage disabled — ignore
    }
  }, []);

  const isActualSuperAdmin = state.roles.includes("super_admin");

  // Defensively clear the flag if the signed-in user isn't a super admin
  // (stale localStorage, account switch) so a non-super account never lands
  // in a half-state. Wait for roles to finish loading first — otherwise the
  // empty-roles window during a page refresh would prematurely exit a
  // persisted preview.
  useEffect(() => {
    if (!state.loading && !isActualSuperAdmin && managerPreview) {
      setManagerPreview(false);
    }
  }, [state.loading, isActualSuperAdmin, managerPreview, setManagerPreview]);

  const effectiveRoles = useMemo<AppRole[]>(() => {
    if (managerPreview && isActualSuperAdmin) {
      // Present as a Manager: drop super_admin AND org_admin (org-wide power),
      // ensure plain admin is present so the normal admin pages still pass
      // their role gate.
      const downgraded = state.roles.filter(
        (r) => r !== "super_admin" && r !== "org_admin",
      );
      if (!downgraded.includes("admin")) downgraded.push("admin");
      return downgraded;
    }
    return state.roles;
  }, [managerPreview, isActualSuperAdmin, state.roles]);

  const signOut = useCallback(async () => {
    setManagerPreview(false);
    await supabase.auth.signOut();
  }, [setManagerPreview]);

  const refreshRoles = useCallback(async () => {
    if (!state.user) return;
    const client = await fetchClientContext(state.user.id);
    const roles: AppRole[] = client ? ["client"] : await fetchRoles(state.user.id);
    setState((prev) => ({ ...prev, roles, client }));
  }, [state.user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signOut,
      refreshRoles,
      managerPreview,
      setManagerPreview,
      effectiveRoles,
    }),
    [state, signOut, refreshRoles, managerPreview, setManagerPreview, effectiveRoles],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
